import { Board, COLS, Coord, Difficulty, Disc, GameStatus, ROWS, getDropRow } from '../engine';
import { el, qs, qsa } from './dom';
import {
  ICON_CHEVRON,
  ICON_RESTART,
  ICON_SOUND_OFF,
  ICON_SOUND_ON,
  ICON_UNDO,
  logoMarkHtml,
} from './icons';

export type Outcome = 'win' | 'lose' | 'draw';

/** Everything the game screen needs to paint itself for one frame. */
export interface GameVM {
  board: Board;
  winningLine: Coord[] | null;
  status: GameStatus;
  current: Disc;
  humanDisc: Disc;
  aiDisc: Disc;
  locked: boolean;
  ghostCol: number | null;
  lastMove: Coord | null;
  scores: { you: number; bot: number };
  difficulty: Difficulty;
  soundOn: boolean;
  canUndo: boolean;
}

export interface GameHandlers {
  onColumn: (col: number) => void;
  onHover: (col: number | null) => void;
  onUndo: () => void;
  onRestart: () => void;
  onToggleSound: () => void;
  onRematch: () => void;
  onMenu: () => void;
}

const RESULT_COPY: Record<Outcome, { title: string; text: string }> = {
  win: { title: 'You win!', text: 'Four in a row — nicely played.' },
  lose: { title: 'Bot wins', text: 'The bot connected four. Care for a rematch?' },
  draw: { title: "It's a draw", text: 'Every cell filled — perfectly even.' },
};

/** Owns the game-screen DOM. Built once; `update` repaints from a view model. */
export class GameView {
  readonly root: HTMLElement;
  private readonly cells: HTMLElement[][] = []; // cells[row][col] → disc element
  private readonly chevrons: HTMLElement[] = [];

  private readonly youCard: HTMLElement;
  private readonly botCard: HTMLElement;
  private readonly youToken: HTMLElement;
  private readonly botToken: HTMLElement;
  private readonly youLabel: HTMLElement;
  private readonly botLabel: HTMLElement;
  private readonly youScore: HTMLElement;
  private readonly botScore: HTMLElement;
  private readonly diffBadge: HTMLElement;
  private readonly soundBtn: HTMLButtonElement;
  private readonly undoBtn: HTMLButtonElement;

  private readonly resultOverlay: HTMLElement;
  private readonly resultDisc: HTMLElement;
  private readonly resultTitle: HTMLElement;
  private readonly resultText: HTMLElement;
  private readonly resultScore: HTMLElement;

  private humanDisc: Disc = 1;
  private aiDisc: Disc = 2;

  constructor(parent: HTMLElement, handlers: GameHandlers) {
    this.root = el(`
      <section class="screen screen--game" aria-label="Game">
        <div class="card game-card">
          <header class="game-header">
            <div class="brand brand--sm">
              ${logoMarkHtml('sm')}
              <span class="brand__name">Connect Four</span>
            </div>
            <span class="diff-badge">Medium</span>
          </header>

          <div class="scoreboard">
            <div class="player player--you">
              <span class="token-wrap"><span class="token"></span></span>
              <span class="player__meta">
                <span class="player__name">You</span>
                <span class="player__state"></span>
              </span>
              <span class="player__score">0</span>
            </div>
            <span class="vs">VS</span>
            <div class="player player--bot">
              <span class="player__score">0</span>
              <span class="player__meta player__meta--right">
                <span class="player__name">Bot</span>
                <span class="player__state"></span>
              </span>
              <span class="token-wrap"><span class="token"></span></span>
            </div>
          </div>

          <div class="board-area">
            <div class="chevron-row" aria-hidden="true">
              ${Array.from({ length: COLS }, () => `<span class="chevron">${ICON_CHEVRON}</span>`).join('')}
            </div>
            <div class="board" aria-label="Connect Four board"></div>
            <div class="board-shadow" aria-hidden="true"></div>
          </div>

          <div class="controls">
            <button type="button" class="btn btn--tile ctrl-undo">${ICON_UNDO} Undo</button>
            <button type="button" class="btn btn--tile ctrl-restart">${ICON_RESTART} Restart</button>
            <button type="button" class="btn btn--tile btn--icon ctrl-sound" aria-label="Toggle sound">${ICON_SOUND_ON}</button>
          </div>
        </div>

        <div class="modal-overlay result-overlay" hidden>
          <div class="modal modal--result" role="dialog" aria-modal="true" aria-live="polite">
            <div class="confetti" aria-hidden="true">
              ${Array.from({ length: 8 }, (_, i) => `<span class="confetti__bit confetti__bit--${i % 4}"></span>`).join('')}
            </div>
            <span class="result-disc token"></span>
            <h2 class="result-title modal__title"></h2>
            <p class="result-text"></p>
            <div class="result-score"></div>
            <div class="result-actions">
              <button type="button" class="btn btn--primary result-rematch">${ICON_RESTART} Rematch</button>
              <button type="button" class="btn btn--ghost result-menu">Back to menu</button>
            </div>
          </div>
        </div>
      </section>
    `);

    this.youCard = qs(this.root, '.player--you');
    this.botCard = qs(this.root, '.player--bot');
    this.youToken = qs(this.youCard, '.token');
    this.botToken = qs(this.botCard, '.token');
    this.youLabel = qs(this.youCard, '.player__state');
    this.botLabel = qs(this.botCard, '.player__state');
    this.youScore = qs(this.youCard, '.player__score');
    this.botScore = qs(this.botCard, '.player__score');
    this.diffBadge = qs(this.root, '.diff-badge');
    this.soundBtn = qs<HTMLButtonElement>(this.root, '.ctrl-sound');
    this.undoBtn = qs<HTMLButtonElement>(this.root, '.ctrl-undo');

    this.resultOverlay = qs(this.root, '.result-overlay');
    this.resultDisc = qs(this.root, '.result-disc');
    this.resultTitle = qs(this.root, '.result-title');
    this.resultText = qs(this.root, '.result-text');
    this.resultScore = qs(this.root, '.result-score');

    this.chevrons.push(...qsa(this.root, '.chevron'));
    this.buildBoard(qs(this.root, '.board'), handlers);
    this.wireControls(handlers);

    parent.appendChild(this.root);
  }

  /** Builds 7 keyboard-operable columns, each holding 6 stacked cells. */
  private buildBoard(boardEl: HTMLElement, handlers: GameHandlers): void {
    for (let row = 0; row < ROWS; row++) this.cells[row] = [];

    for (let col = 0; col < COLS; col++) {
      const column = el(`<div class="column" role="button" tabindex="0"></div>`);
      column.dataset.col = String(col);
      column.setAttribute('aria-label', `Drop in column ${col + 1}`);

      const drop = () => handlers.onColumn(col);
      column.addEventListener('click', drop);
      column.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          drop();
        }
      });
      column.addEventListener('mouseenter', () => handlers.onHover(col));
      column.addEventListener('focus', () => handlers.onHover(col));
      column.addEventListener('blur', () => handlers.onHover(null));

      for (let row = 0; row < ROWS; row++) {
        const cell = el(`<div class="cell"><div class="disc is-empty"></div></div>`);
        this.cells[row][col] = qs(cell, '.disc');
        column.appendChild(cell);
      }
      boardEl.appendChild(column);
    }

    boardEl.addEventListener('mouseleave', () => handlers.onHover(null));
  }

  private wireControls(handlers: GameHandlers): void {
    this.undoBtn.addEventListener('click', () => handlers.onUndo());
    qs<HTMLButtonElement>(this.root, '.ctrl-restart').addEventListener('click', () => handlers.onRestart());
    this.soundBtn.addEventListener('click', () => handlers.onToggleSound());
    qs<HTMLButtonElement>(this.root, '.result-rematch').addEventListener('click', () => handlers.onRematch());
    qs<HTMLButtonElement>(this.root, '.result-menu').addEventListener('click', () => handlers.onMenu());
  }

  /** Repaints the whole screen (board, scoreboard, chevrons, controls) from `vm`. */
  update(vm: GameVM): void {
    this.humanDisc = vm.humanDisc;
    this.aiDisc = vm.aiDisc;

    this.paintBoard(vm);
    this.paintChevrons(vm);
    this.paintScoreboard(vm);

    this.diffBadge.textContent = cap(vm.difficulty);
    this.undoBtn.disabled = !vm.canUndo;
    this.soundBtn.innerHTML = vm.soundOn ? ICON_SOUND_ON : ICON_SOUND_OFF;
    this.soundBtn.classList.toggle('is-off', !vm.soundOn);
    this.soundBtn.setAttribute('aria-pressed', String(vm.soundOn));
  }

  private paintBoard(vm: GameVM): void {
    const winSet = new Set((vm.winningLine ?? []).map((c) => `${c.row},${c.col}`));
    const ghost = this.ghostCell(vm);

    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const disc = vm.board[row][col];
        const node = this.cells[row][col];
        let cls = 'disc';

        if (ghost && ghost.row === row && ghost.col === col) {
          cls += vm.current === 1 ? ' is-ghost-red' : ' is-ghost-yellow';
        } else {
          cls += ` ${stateClass(disc)}`;
          if (winSet.has(`${row},${col}`)) cls += ' is-win';
          if (vm.lastMove && vm.lastMove.row === row && vm.lastMove.col === col && disc !== 0) {
            cls += ' is-drop';
          }
        }
        node.className = cls;
      }
    }
  }

  /** Where a disc would land in the hovered column, or null if no live preview applies. */
  private ghostCell(vm: GameVM): Coord | null {
    if (vm.ghostCol === null) return null;
    if (vm.status !== 'playing' || vm.locked || vm.current !== vm.humanDisc) return null;
    const row = getDropRow(vm.board, vm.ghostCol);
    return row === null ? null : { row, col: vm.ghostCol };
  }

  private paintChevrons(vm: GameVM): void {
    const ghost = this.ghostCell(vm);
    const colorClass = vm.humanDisc === 1 ? 'is-red' : 'is-yellow';
    this.chevrons.forEach((chev, col) => {
      const active = !!ghost && ghost.col === col;
      chev.className = `chevron${active ? ` is-active ${colorClass}` : ''}`;
    });
  }

  private paintScoreboard(vm: GameVM): void {
    this.youToken.className = `token ${tokenClass(vm.humanDisc)}`;
    this.botToken.className = `token ${tokenClass(vm.aiDisc)}`;
    this.youScore.textContent = String(vm.scores.you);
    this.botScore.textContent = String(vm.scores.bot);

    const playing = vm.status === 'playing';
    const youTurn = playing && vm.current === vm.humanDisc;
    const botTurn = playing && vm.current === vm.aiDisc;

    // Card carries its player's colour so CSS can tint the border, label, and pulse.
    this.youCard.className = `player player--you ${colorWord(vm.humanDisc)}${youTurn ? ' is-active' : ''}`;
    this.botCard.className = `player player--bot ${colorWord(vm.aiDisc)}${botTurn ? ' is-active' : ''}`;
    qs(this.youCard, '.token-wrap').className = `token-wrap ${pulseClass(vm.humanDisc)}`;
    qs(this.botCard, '.token-wrap').className = `token-wrap ${pulseClass(vm.aiDisc)}`;

    this.youLabel.textContent = !playing ? 'Game over' : youTurn ? 'Your turn' : 'Waiting';
    this.botLabel.textContent = !playing ? 'Game over' : botTurn ? 'Thinking…' : 'Waiting';
  }

  /** Pops the win/lose/draw modal. Colours follow the current disc mapping. */
  showResult(outcome: Outcome, scores: { you: number; bot: number }): void {
    const copy = RESULT_COPY[outcome];
    this.resultTitle.textContent = copy.title;
    this.resultText.textContent = copy.text;

    const disc =
      outcome === 'win' ? this.humanDisc : outcome === 'lose' ? this.aiDisc : null;
    this.resultDisc.className = `result-disc token ${disc === null ? 'token--neutral' : tokenClass(disc)}`;

    this.resultScore.innerHTML =
      `<span class="result-score__you">You ${scores.you}</span>` +
      `<span class="result-score__sep">·</span>` +
      `<span class="result-score__bot">Bot ${scores.bot}</span>`;

    this.resultOverlay.classList.toggle('is-celebrating', outcome === 'win');
    this.resultOverlay.hidden = false;
  }

  hideResult(): void {
    this.resultOverlay.hidden = true;
    this.resultOverlay.classList.remove('is-celebrating');
  }

  show(): void {
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
    this.hideResult();
  }
}

function stateClass(cell: number): string {
  return cell === 1 ? 'is-red' : cell === 2 ? 'is-yellow' : 'is-empty';
}

function tokenClass(disc: Disc): string {
  return disc === 1 ? 'token--red' : 'token--yellow';
}

function pulseClass(disc: Disc): string {
  return disc === 1 ? 'pulse--red' : 'pulse--yellow';
}

function colorWord(disc: Disc): string {
  return disc === 1 ? 'is-red' : 'is-yellow';
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
